import { useParams } from 'react-router';
import {
  SitrepSectionType,
  useSitrepV2,
  useUpdateSitrepSectionV2,
} from '../DisasterGPT/hooks/useSitrepsV2';
import PageTitle from '../headers/PageTitle';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import dayjs from 'dayjs';
import { SharedIcon } from '../SharedIcon/SharedIcon';
import TinyMCEWithFormController from '../DesignSystems/New/TinyMCEWithFormController';
import { useForm } from 'react-hook-form';
import { useToggle } from 'react-use';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

type SitrepSectionForm = {
  sitrepSectionContent: string;
};
type SitrepSectionProps = {
  section: SitrepSectionType;
  sitrepId: UUID;
};
const SitrepSection = ({ section, sitrepId }: SitrepSectionProps) => {
  const [isEditing, setIsEditing] = useToggle(false);
  const { mutate } = useUpdateSitrepSectionV2();
  const { handleSubmit, control } = useForm<SitrepSectionForm>();
  const handleSubmitForm = (data: SitrepSectionForm) => {
    mutate({
      sitrepId,
      sectionId: section.id,
      updatedFields: { sectionText: sanitizeHtml(data.sitrepSectionContent) },
    });
    setIsEditing(false);
  };

  return (
    <div className="sitrep-section">
      <div className="sitrep-section-header d-flex flex-row justify-content-between">
        <p className="sitrep-section-header-text align-self-center">
          {`Reported Event: ${section.name} | ${dayjs(section.section_versions[0].timestamp)}`}
        </p>
        <StylishNewButton className="button--icon" onClick={setIsEditing}>
          <SharedIcon iconName="edit" color="light" />
        </StylishNewButton>
      </div>
      <div className="sitrep-section-content">
        {isEditing ? (
          <form onSubmit={handleSubmit(handleSubmitForm)}>
            <TinyMCEWithFormController
              name="sitrepSectionContent"
              className="sitrep-section-content-MCE"
              defaultValue={
                section.section_versions[0].sitrep_section_text ?? undefined
              }
              control={control}
              readOnly={false}
              placeholder="Write your report here..."
            />
            <div className="button-group d-flex flex-row justify-content-end">
              <StylishNewButton type="submit" className="button--primary">
                Submit Changes
              </StylishNewButton>
            </div>
          </form>
        ) : (
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                section.section_versions[0].sitrep_section_text ?? ''
              ),
            }}
          />
        )}
      </div>
    </div>
  );
};

const noop = () => {};
const SingleSitrep = () => {
  const { sitrepId } = useParams();
  const { data: sitrep, isLoading, isError, isSuccess } = useSitrepV2(sitrepId);

  if (isLoading) {
    return <>Loading...</>;
  }

  if (isError || !isSuccess) {
    return <>Error loading Sitrep</>;
  }

  const sortedSitrepSections = sitrep.sections.sort(
    (a, b) => a.order - b.order
  );

  return (
    <div className="site-main">
      <div className="container-fluid p-3">
        <PageTitle title={sitrep.name} parents={['Sitreps']} />
        <div className="d-flex gap-3">
          <StylishNewButton
            className="button--primary color-black"
            onClick={noop}
          >
            Edit Report
          </StylishNewButton>
          <StylishNewButton
            className="button--primary color-black"
            onClick={noop}
          >
            Download
          </StylishNewButton>
          <StylishNewButton
            className="button--primary color-black"
            onClick={noop}
          >
            Create Incident
          </StylishNewButton>
        </div>
        <div className="d-flex flex-column gap-3 mt-3">
          {Array.isArray(sortedSitrepSections) &&
            sortedSitrepSections.length > 0 &&
            sortedSitrepSections.map((section) => (
              <SitrepSection
                key={section.id}
                section={section}
                sitrepId={sitrep.id}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default SingleSitrep;
